import React, { useState } from 'react';
import { FaTimes, FaUser, FaTruck, FaSpinner } from 'react-icons/fa';

const AssignJobModal = ({ isOpen, onClose, job, availableDrivers, onAssign }) => {
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [assigning, setAssigning] = useState(false);

  if (!isOpen || !job) return null;

  const handleAssign = async () => {
    if (!selectedDriverId) {
      return;
    }

    setAssigning(true);
    try {
      await onAssign(job.id, selectedDriverId);
      onClose();
    } catch (error) {
      console.error('Error assigning job:', error);
    } finally {
      setAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b bg-blue-600 text-white rounded-t-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <FaTruck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Assign Job</h2>
              <p className="text-blue-100 text-sm">Select a driver for this job</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Job Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-800 mb-2">Job Details</h3>
            <div className="text-sm text-gray-600">
              <p><strong>Route:</strong> {job.route || `${job.pickupLocation} → ${job.deliveryLocation}`}</p>
              <p><strong>Price:</strong> {job.priceFormatted || `$${job.price || 0}`}</p>
              <p><strong>Category:</strong> {job.category || 'Standard'}</p>
            </div>
          </div>

          {/* Driver Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Driver *
            </label>
            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              required
            >
              <option value="">Choose a driver...</option>
              {availableDrivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.firstName} {driver.lastName} - {driver.phone || 'No phone'}
                </option>
              ))}
            </select>
          </div>

          {/* Available Drivers Count */}
          <div className="text-sm text-gray-500 text-center">
            {availableDrivers.length > 0 ? (
              <span className="text-green-600">
                {availableDrivers.length} driver{availableDrivers.length > 1 ? 's' : ''} available
              </span>
            ) : (
              <span className="text-red-600">No drivers available</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!selectedDriverId || assigning || availableDrivers.length === 0}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {assigning ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Assigning...
              </>
            ) : (
              <>
                <FaUser className="mr-2" />
                Assign Job
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignJobModal;
